import React, { useContext } from 'react';
import CloseButton from '@/internals/CloseButton';
import IconButton from '../IconButton';
import Box, { BoxProps } from '@/internals/Box';
import { forwardRef, createChainedFunction } from '@/internals/utils';
import { useStyles } from '@/internals/hooks';
import { ModalContext } from './ModalContext';

export interface ModalHeaderProps extends BoxProps {
  /** Primary content */
  children?: React.ReactNode;

  /** Display close button */
  closeButton?: boolean;

  /** Called when Modal is hidden */
  onClose?: (event: React.MouseEvent) => void;
}

const ModalHeader = forwardRef<'div', ModalHeaderProps>((props, ref) => {
  const {
    as,
    classPrefix = 'modal-header',
    className,
    closeButton = true,
    children,
    onClose,
    ...rest
  } = props;
  const { merge, withPrefix, prefix } = useStyles(classPrefix);
  const classes = merge(className, withPrefix());

  const context = useContext(ModalContext);
  const { onModalClose } = context || {};

  return (
    <Box as={as} {...rest} ref={ref} className={classes}>
      {closeButton && (
        <CloseButton
          as={IconButton}
          className={prefix('close')}
          onClick={createChainedFunction(onClose, onModalClose)}
        />
      )}
      {children}
    </Box>
  );
});

ModalHeader.displayName = 'ModalHeader';

export default ModalHeader;
